/**
 * galleryUploadManager — GAL-8's app-side singleton around
 * GalleryUploadEngine.
 *
 * This is the file that makes uploads SURVIVE NAVIGATION: the
 * engine + all its transfer state live at module level, so route
 * changes can unmount every gallery page without touching a byte
 * in flight. Pages talk to it two ways:
 *
 *   • enqueueGalleryMedia(...) — fire a batch and walk away.
 *   • useGalleryUploads() (src/hooks) — subscribe for display.
 *
 * Responsibilities that used to live (and die) inside page
 * components:
 *   • storage transfer (fast single-PUT under 6 MB, TUS above —
 *     post-prep gallery JPEGs are ~1–3 MB so nearly everything
 *     rides the fast path),
 *   • gallery_photos inserts (incl. the GAL-7 missing-migration
 *     strip-and-retry — FIX-2 moved the migration filename into the
 *     console and left the user a plain-language note),
 *   • Mux video handoff + BOUNDED transcode polling (FIX-2: the old
 *     poll gave up after ~12 minutes and reported success anyway),
 *   • completion toasts, Nova tagging, and react-query cache
 *     invalidation — via the queryClient ATTACHED AT BOOTSTRAP
 *     (App.tsx), never a page hook, so a finished background batch
 *     refreshes the hub even if the page that started it is gone.
 *   • the beforeunload guard: closing the TAB mid-batch warns;
 *     in-app navigation never does (SPA routing doesn't fire it).
 */
import type { QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
// P1-2 — PostgrestError is not an Error; parseSupabaseError knows its
// shape. Used by the storage gate's fail-open logging.
import { debug } from '@/lib/debug';
import { resumableUpload } from '@/lib/resumableUpload';
import { thumbnailUrl } from '@/lib/imageTransform';
import { prepImageInWorker } from '@/lib/uploads/prepWorkerPool';
import {
  GalleryUploadEngine,
  type BatchResult,
  type EngineSnapshot,
} from '@/lib/uploads/galleryUploadEngine';
import {
  ensureGalleryDelivery,
  type GalleryPhoto,
} from '@/services/galleries';
import { createMuxUpload, uploadVideoToMux } from '@/services/deliveryService';
// FIX-2 — the bounded, honest transcode wait. Replaces the old
// ~12-minute poll that reported SUCCESS on give-up.
import { watchMuxProcessing } from '@/lib/uploads/muxProcessingWatcher';
import { tagPhotosBatch, type TagPhotoInput } from '@/services/novaDelivery';

// GAL-3 fix — public bucket. Private `deliverables` + getPublicUrl
// = "Bucket not found" 404s on every photo click.
const BUCKET = 'delivery-photos';
/** Files at/above this ride TUS; below it a single PUT is faster. */
const RESUMABLE_THRESHOLD = 6 * 1024 * 1024;

// Same normalization the editor used — Supabase Storage rejects
// exotic characters in object keys.
const toSafeStorageFilename = (name: string) => {
  const lastDot = name.lastIndexOf('.');
  const base = lastDot > 0 ? name.slice(0, lastDot) : name;
  const ext = lastDot > 0 ? name.slice(lastDot) : '';
  const normalized = base
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return `${normalized || 'file'}${ext}`;
};

const convertHeicToJpeg = async (blob: Blob): Promise<Blob> => {
  const heic2any = (await import('heic2any')).default;
  const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.92 });
  return Array.isArray(converted) ? converted[0] : converted;
};

// ── QueryClient attachment ───────────────────────────────────────
// The manager outlives every page, so invalidation can't depend on
// a page's useQueryClient. App.tsx attaches the app-wide client at
// bootstrap; until then invalidations queue up (a batch physically
// can't finish before the app has booted, but belt + suspenders).

let queryClient: QueryClient | null = null;
let pendingInvalidations: string[][] = [];

export function attachUploadQueryClient(qc: QueryClient) {
  queryClient = qc;
  if (pendingInvalidations.length > 0) {
    const keys = pendingInvalidations;
    pendingInvalidations = [];
    for (const key of keys) qc.invalidateQueries({ queryKey: key });
  }
}

function invalidate(keys: (string | undefined)[][]) {
  const clean = keys.map((k) => k.filter((p): p is string => p !== undefined));
  if (!queryClient) {
    pendingInvalidations.push(...clean);
    return;
  }
  for (const key of clean) queryClient.invalidateQueries({ queryKey: key });
}

// Throttled per-gallery grid refresh so photos appear WHILE the
// batch runs without refetching on all 40 inserts.
const photoRefreshTimers = new Map<string, number>();
function throttledPhotoRefresh(galleryId: string) {
  if (photoRefreshTimers.has(galleryId)) return;
  const handle = window.setTimeout(() => {
    photoRefreshTimers.delete(galleryId);
    invalidate([
      ['gallery-photos', galleryId],
      ['studio-galleries'],
    ]);
  }, 1500);
  photoRefreshTimers.set(galleryId, handle);
}

// ── GAL-7 missing-migration latch (mirrors useAddGalleryPhoto) ───
let warnedMissingDimensionColumns = false;

async function insertPhotoRow(row: {
  galleryId: string;
  fileUrl: string;
  name: string;
  sortOrder: number;
  width: number | null;
  height: number | null;
}): Promise<{ id: string }> {
  const payload = {
    gallery_id: row.galleryId,
    file_url: row.fileUrl,
    thumbnail_url: thumbnailUrl(row.fileUrl),
    name: row.name,
    sort_order: row.sortOrder,
    width: row.width,
    height: row.height,
  };
  const { data, error } = await supabase
    .from('gallery_photos')
    .insert(payload)
    .select()
    .single();
  if (!error) return data as GalleryPhoto;

  // PGRST204 = PostgREST "column not found in schema cache";
  // 42703 = Postgres undefined_column. Either means the GAL-7
  // dimensions migration is missing from the live DB — strip and
  // retry so uploads never break, but say so LOUDLY, once
  // (unapplied migrations are this project's #1 failure mode).
  const columnMissing =
    (row.width != null || row.height != null) &&
    (error.code === 'PGRST204' || error.code === '42703') &&
    /width|height/i.test(error.message ?? '');
  if (!columnMissing) throw error;
  if (!warnedMissingDimensionColumns) {
    warnedMissingDimensionColumns = true;
    // FIX-2 — the DIAGNOSTIC (which migration, which columns) belongs
    // in the console, where a developer will find it. What used to be
    // here was a red toast naming a raw migration filename, which on
    // stage reads as "the product is broken" for what is actually a
    // cosmetic degradation: the photos upload and display fine, the
    // grid just can't reserve their aspect ratio ahead of load.
    //
    // ROOT CAUSE (for Rennan): migration
    // 20260804090000_gal7_gallery_photo_dimensions has not been
    // applied to this database. Apply it and this path disappears.
    console.error(
      '[GAL-7] gallery_photos.width/height missing — migration ' +
        '20260804090000_gal7_gallery_photo_dimensions is not applied to this ' +
        'database. Photos are being saved WITHOUT dimensions; grids will ' +
        'reflow as images load. Original error:',
      error,
    );
    toast.warning('Photos saved — grid may shift while loading', {
      description:
        'Your photos uploaded fine. They’re missing their size info, so thumbnails settle into place as they load. Nothing to do — everything is saved.',
      duration: 8000,
    });
  }
  const { width: _w, height: _h, ...withoutDims } = payload;
  const retry = await supabase
    .from('gallery_photos')
    .insert(withoutDims)
    .select()
    .single();
  if (retry.error) throw retry.error;
  return retry.data as GalleryPhoto;
}

// ── Mux transcode poll (module-level: survives navigation) ───────
//
// FIX-2 — this used to be a hand-rolled setInterval that, after
// ~120 attempts (~12 minutes), called
// `engine.resolveProcessingItem(itemId, true)` — i.e. it reported
// SUCCESS for a video it had never seen finish. A studio watching
// the tray got a green check for a video that might not exist. That
// is the worst possible failure mode for a delivery product.
//
// Now it delegates to the shared bounded watcher and maps outcomes
// honestly: ready → done, errored/missing → failed with a specific
// message, timeout → 'stalled' (uploaded, unconfirmed) which the
// tray renders in amber with an explanation, never a check.
function pollVideoReady(
  galleryId: string,
  deliveryId: string,
  deliveryFileId: string,
  itemId: string,
) {
  const refreshGallery = () =>
    invalidate([
      ['gallery-videos', galleryId],
      ['public-gallery', galleryId],
    ]);

  void watchMuxProcessing({
    deliveryId,
    deliveryFileId,
    onTick: ({ hint }) => engine.updateProcessingMessage(itemId, hint),
  })
    .then((result) => {
      if (result.outcome === 'ready') {
        engine.resolveProcessingItem(itemId, true);
        refreshGallery();
        return;
      }
      if (result.outcome === 'errored' || result.outcome === 'missing') {
        engine.resolveProcessingItem(itemId, false, result.message);
        refreshGallery();
        toast.error('Video processing failed', {
          description: result.message,
          duration: 12000,
        });
        return;
      }
      // timeout / cancelled — undecided. Say exactly that.
      engine.stallProcessingItem(itemId, result.message);
      refreshGallery();
      toast.warning('Video still processing', {
        description: result.message,
        duration: 12000,
      });
    })
    .catch((err) => {
      // watchMuxProcessing resolves rather than rejects, but a module
      // -level unhandled rejection is exactly what FIX-2 removes.
      console.error('[FIX-2] gallery transcode watch failed', err);
      engine.stallProcessingItem(
        itemId,
        'Uploaded, but we lost track of the transcode. Reopen the gallery in a minute — the video appears on its own once Mux finishes.',
      );
      refreshGallery();
    });
}

// ── Batch completion: toasts + tagging + cache refresh ───────────
//
// FIX-2 — a video retry (galleryUploads.retryVideo) un-settles its
// batch, so this callback can fire more than once for the same batch.
// Cache invalidation and the VIDEO toasts should repeat (they're the
// retry's result); the photo summary and Nova tagging must not.
/** Batches whose photos have already gone to Nova. */
const taggedBatches = new Set<string>();
/** Batches that have already reported their photo summary. */
const photoSummaryShown = new Set<string>();

function onBatchSettled(result: BatchResult) {
  const { galleryId, projectId, insertedPhotos, photosFailed, videosUploaded, videosFailed } =
    result;
  const firstSettle = !photoSummaryShown.has(result.batch.id);
  photoSummaryShown.add(result.batch.id);

  // The manager OUTLIVES the page — these invalidations are what
  // make a background-finished batch show up on the hub, the
  // editor, the workspace tab and the public page without any
  // surviving component. ['galleries'] partial-matches the
  // per-project key family.
  invalidate([
    ['gallery-photos', galleryId],
    ['studio-galleries'],
    ['studio-gallery', galleryId],
    ['galleries'],
    ['gallery-videos', galleryId],
    ['public-gallery', galleryId],
  ]);

  const photosSaved = insertedPhotos.length;
  const photoTotal = photosSaved + photosFailed;
  const galleryLabel = result.batch.galleryName || 'gallery';

  if (photoTotal > 0 && firstSettle) {
    if (photosFailed === 0) {
      toast.success(
        photosSaved === 1
          ? `Uploaded 1 photo to "${galleryLabel}"`
          : `Uploaded ${photosSaved} photos to "${galleryLabel}"`,
      );
    } else if (photosSaved === 0) {
      toast.error(`All ${photosFailed} uploads failed. Open the upload tray for details.`);
    } else {
      toast.warning(
        `Uploaded ${photosSaved} of ${photoTotal} to "${galleryLabel}". ${photosFailed} failed — see the upload tray.`,
      );
    }
  }
  if (videosUploaded > 0) {
    toast.success(
      videosUploaded === 1
        ? 'Video uploaded — processing now'
        : `${videosUploaded} videos uploaded — processing now`,
      { description: 'They’ll appear in the gallery as soon as Mux finishes transcoding.' },
    );
  }
  if (videosFailed > 0) {
    // FIX-2 — this used to point at "the gallery", where no retry
    // existed. It now points at the tray, where one does.
    toast.error(
      `${videosFailed} video${videosFailed === 1 ? '' : 's'} didn’t upload`,
      {
        description:
          'Open the upload tray (bottom-right) and press Retry — your file is still selected.',
        duration: 10000,
      },
    );
  }

  // Wave 5 Feature 1 — Nova tagging, fire-and-forget, chunked to
  // the edge function's 20-per-call cap.
  //
  // FIX-2 — a video retry un-settles the batch, so this callback can
  // fire more than once per batch. Tagging is idempotent but not free;
  // only ever do it on the first settle.
  if (insertedPhotos.length > 0 && !taggedBatches.has(result.batch.id)) {
    taggedBatches.add(result.batch.id);
    const tagInputs: TagPhotoInput[] = insertedPhotos.map((p) => ({
      photo_id: p.id,
      image_url: p.url,
    }));
    const CHUNK = 20;
    for (let i = 0; i < tagInputs.length; i += CHUNK) {
      tagPhotosBatch(tagInputs.slice(i, i + CHUNK)).catch((err) => {
        // Retag is idempotent + cosmetic — log, don't toast.
        console.warn('[nova-tag-photo] batch failed', err);
      });
    }
  }
  void projectId; // (kept in the result shape for future per-project keys)
}

// ── The engine instance ──────────────────────────────────────────

const engine = new GalleryUploadEngine(
  {
    prepPhoto: async (file) => {
      let input = file as File;
      // HEIC drops convert first (some browsers hand over an empty
      // MIME for .heic, so extension-sniff like the editor did).
      if (/\.heic$/i.test(input.name)) {
        const jpegBlob = await convertHeicToJpeg(input);
        input = new File([jpegBlob], input.name.replace(/\.heic$/i, '.jpg'), {
          type: 'image/jpeg',
        });
      }
      // Worker-backed prep (decode + downscale + dims) with inline
      // fallback — see prepWorkerPool.
      return prepImageInWorker(input);
    },
    uploadPhoto: async ({ file, path, onProgress }) => {
      // `path` from the engine is the DIRECTORY prefix; the object
      // key uses the PREPPED name (prep may rename .png/.heic to
      // .jpg) — same behavior the page-bound flows had.
      const objectPath = `${path}/${Date.now()}-${toSafeStorageFilename(file.name)}`;
      if (file.size >= RESUMABLE_THRESHOLD) {
        const result = await resumableUpload({
          bucket: BUCKET,
          path: objectPath,
          file: file as File,
          onProgress,
        });
        return { url: result.url, path: result.path };
      }
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .upload(objectPath, file as File, { cacheControl: '3600', upsert: false });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(data.path);
      return { url: urlData.publicUrl, path: data.path };
    },
    insertPhotoRow,
    ensureVideoDelivery: ensureGalleryDelivery,
    createVideoUpload: (deliveryId, fileName, fileSize) =>
      createMuxUpload(deliveryId, fileName, fileSize),
    uploadVideo: (url, file, onProgress) => uploadVideoToMux(url, file as File, onProgress),
    onBatchSettled,
    onPhotoSaved: throttledPhotoRefresh,
    onVideoUploaded: (galleryId, deliveryId, deliveryFileId, itemId) => {
      invalidate([['gallery-videos', galleryId]]);
      pollVideoReady(galleryId, deliveryId, deliveryFileId, itemId);
    },
  },
  {
    prepConcurrency: 3,
    // Post-prep gallery JPEGs are ~1–3 MB — 5 concurrent small PUTs
    // amortize request latency without stressing the edge (the old
    // page-bound path used 4 with prep fully serialized in front).
    uploadConcurrency: 5,
    maxAttempts: 3,
  },
);

// ── beforeunload guard ───────────────────────────────────────────
// Real tab-close mid-transfer loses the batch — warn. In-app
// navigation never triggers beforeunload, so SPA routing stays
// silent, which is the whole point of the manager.
const beforeUnload = (e: BeforeUnloadEvent) => {
  if (!engine.hasInFlightWork()) return;
  e.preventDefault();
  // Chrome still requires returnValue to show the prompt.
  e.returnValue = '';
};
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', beforeUnload);
}

// ── P1-2 · Pre-upload storage gate ───────────────────────────────
//
// `can_upload_bytes(org_id, bytes)` has been the server's storage
// enforcement point since SUB-1 Phase 3 (COST-1 gave Business a real
// 4 TiB cap), and iOS has called it before every batch since Phase 3C
// (GalleryUploader.swift → StorageGate) — but the WEB upload path
// never did. A web studio at cap only found out via opaque per-file
// storage errors mid-batch. Same contract as iOS:
//
//   • Sum the batch's PHOTO bytes (videos ride Mux direct upload, not
//     Supabase Storage — org_storage_used_bytes doesn't count them,
//     so neither does the gate). Pre-prep sizes, so the estimate is
//     conservative — prep only ever shrinks files.
//   • FAIL OPEN on transport/shape errors: the server still enforces;
//     a network blip must not strand an upload. Logged via debug.warn
//     — degraded, not silent.
//   • FAIL CLOSED on an explicit `allowed: false`: refuse the batch
//     up front with the storage copy + a working "See plans" CTA
//     (the inline-nudge voice of UpgradeNudgeCard's `storage` reason;
//     the card itself is a React component and this module outlives
//     every page, so the toast carries the CTA).
//
// The gate lives HERE, at the single choke point every surface
// (NewGalleryDialog / GalleryEditor / GalleryTab) funnels through —
// the P0-2 lesson: one place to enforce beats three places to forget.

interface StorageGateDecision {
  allowed: boolean;
  usedBytes: number;
  capBytes: number;
  /** False when the RPC was unreachable / returned an unexpected
   *  shape — the numbers are placeholders and `allowed` is a
   *  fail-open, not a verdict. */
  authoritative: boolean;
}

const GATE_FAIL_OPEN: StorageGateDecision = {
  allowed: true,
  usedBytes: 0,
  capBytes: 0,
  authoritative: false,
};

async function checkStorageGate(
  orgId: string,
  bytes: number,
): Promise<StorageGateDecision> {
  try {
    const { data, error } = await supabase.rpc('can_upload_bytes', {
      p_org_id: orgId,
      p_bytes: Math.max(0, Math.round(bytes)),
    });
    if (error) {
      debug.warn(
        `[P1-2] storage gate unreachable — allowing upload (server still enforces): ${debug.parseSupabaseError(error)}`,
      );
      return GATE_FAIL_OPEN;
    }
    const payload = (data ?? null) as {
      allowed?: unknown;
      used_bytes?: unknown;
      cap_bytes?: unknown;
    } | null;
    if (!payload || typeof payload.allowed !== 'boolean') {
      debug.warn(
        '[P1-2] storage gate returned an unexpected shape — allowing upload (server still enforces)',
      );
      return GATE_FAIL_OPEN;
    }
    return {
      allowed: payload.allowed,
      usedBytes: Number(payload.used_bytes ?? 0) || 0,
      capBytes: Number(payload.cap_bytes ?? 0) || 0,
      authoritative: true,
    };
  } catch (err) {
    debug.warn(
      `[P1-2] storage gate threw — allowing upload (server still enforces): ${debug.parseSupabaseError(err)}`,
    );
    return GATE_FAIL_OPEN;
  }
}

/** Binary units to match the server's caps (500 GiB / 1 TiB / 4 TiB)
 *  and iOS's ByteCountFormatter(.binary). */
function formatBytes(n: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = Math.max(0, n);
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value >= 100 || i === 0 ? Math.round(value) : value.toFixed(1)} ${units[i]}`;
}

// ── Public API ───────────────────────────────────────────────────

export interface GalleryMediaBatch {
  galleryId: string;
  galleryName: string;
  /// GAL-14 — null (or '') for a gallery with no project. `enqueue`
  /// swaps in a literal 'standalone' path segment; see below.
  projectId: string | null;
  orgId: string;
  userId: string;
  /** sort_order for the first photo (usually current photo count). */
  startingSortOrder: number;
  imageFiles: File[];
  videoFiles: File[];
}

export const galleryUploads = {
  /**
   * Queue a media batch. Returns immediately — the caller can
   * navigate away, close the dialog, whatever; the tray shows
   * progress and the manager finishes the job.
   *
   * P1-2 — the batch now clears the storage gate (can_upload_bytes)
   * before any byte moves, so the engine enqueue happens a beat
   * later. Nothing was using the returned batch id (verified across
   * src/), so the signature is void. On an explicit cap refusal the
   * whole batch is declined with an upgrade toast — matching iOS,
   * which fails every payload rather than uploading half a shoot.
   */
  enqueue(batch: GalleryMediaBatch): void {
    // GAL-14 — a gallery no longer has to belong to a project, and this
    // path is POSITIONAL: `org/project/galleries/gallery/file`, with the
    // legacy deliverables policy reading foldername[3] and [4] by index.
    // An undefined projectId would interpolate to `org//galleries/...`,
    // shifting every segment left and producing an empty path component
    // that Supabase Storage rejects outright. A literal keeps the shape.
    const projectSegment = batch.projectId || 'standalone';
    const dir = `${batch.orgId}/${projectSegment}/galleries/${batch.galleryId}`;
    void (async () => {
      const photoBytes = batch.imageFiles.reduce((sum, f) => sum + f.size, 0);
      if (photoBytes > 0 && batch.orgId) {
        const decision = await checkStorageGate(batch.orgId, photoBytes);
        if (decision.authoritative && !decision.allowed) {
          // Explicit denial → fail CLOSED, before any transfer.
          // Outcome-led copy in UpgradeNudgeCard's `storage` voice,
          // CTA to the same billing surface its "See plans" uses.
          toast.error('Your galleries have outgrown your storage', {
            description: `This batch needs ${formatBytes(photoBytes)}, and you've used ${formatBytes(decision.usedBytes)} of ${formatBytes(decision.capBytes)}. Grow your plan to keep uploading — nothing was sent.`,
            duration: 15000,
            action: {
              label: 'See plans',
              onClick: () => {
                // Module-level code — no router here. A full
                // navigation to billing is acceptable for this rare,
                // deliberate click.
                window.location.href = '/settings?tab=billing';
              },
            },
          });
          return;
        }
      }
      engine.enqueue({
        galleryId: batch.galleryId,
        galleryName: batch.galleryName,
        projectId: batch.projectId,
        userId: batch.userId,
        startingSortOrder: batch.startingSortOrder,
        photos: batch.imageFiles.map((file) => ({
          file,
          name: file.name,
          path: dir,
        })),
        videos: batch.videoFiles.map((file) => ({ file, name: file.name })),
      });
    })();
  },
  subscribe: engine.subscribe,
  getSnapshot: (): EngineSnapshot => engine.getSnapshot(),
  clearSettled: () => engine.clearSettled(),
  hasInFlightWork: () => engine.hasInFlightWork(),
  /**
   * FIX-2 — retry a failed VIDEO from the tray using the File the
   * studio already picked. Returns false if the item is gone (batch
   * cleared) so the caller can say so instead of silently no-opping.
   */
  retryVideo: (itemId: string): boolean => engine.retryVideo(itemId),
  /**
   * P2-1 — the photo equivalent. Re-enters the pipeline at prep with
   * the retained File; returns false if the item is gone.
   */
  retryPhoto: (itemId: string): boolean => engine.retryPhoto(itemId),
};

export type { EngineSnapshot } from '@/lib/uploads/galleryUploadEngine';
